import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import styled, { keyframes } from 'styled-components';
import moment from 'moment';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import FetchModule from '../../Share/Network/FetchModule';

export default function SalesPeriodBox(props) {
  const {
    startDate,
    endDate,
    settlementCycle,
    settlements,
    settledMerchantUids,
    yearSales,
    isOpenPartnerGymSales,
    setIsYearLoading
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();
  const history = useHistory();

  const [monthData, setMonthData] = useState([]);
  const [periodSalesPrice, setPeriodSalesPrice] = useState(0);
  const [settlementPrice, setSettlementPrice] = useState(0);
  const [expectedSettlementPrice, setExpectedSettlementPrice] = useState(0);
  const [holidays, setHolidays] = useState(new Set());
  const [animationKey, setAnimationKey] = useState(0);

  // 매출 순위에 따른 bar 색상 (밝음 → 어두움) //
  const barColors = [
    theme.brand500, theme.brand550, theme.brand600, theme.brand650, theme.brand700,
    theme.brand750, theme.brand800, theme.brand850, theme.brand900
  ];

  // 공휴일 데이터 조회 //
  useEffect(() => {
    const start = startDate.clone().subtract(10, 'days').format('YYYY-MM-DD');
    const end = endDate.clone().add(10, 'days').format('YYYY-MM-DD');

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('holiday?startDate=' + start + '&endDate=' + end, 'GET');

        if (responseData.status === 200) {
          const holidaySet = new Set();

          for (const holiday of responseData.data.holidays) {
            holidaySet.add(moment(holiday.holidayDate).format('YYYY-MM-DD'));
          }

          setHolidays(holidaySet);
        }
        else if (responseData.status === 401) {
          history.push('/');
        }
      }
    )();
  }, [startDate, endDate]);

  // 주말/공휴일 체크 //
  function isOffDay(m) {
    const dow = m.day(); // 0=일, 6=토
    if (dow === 0 || dow === 6) return true; // 주말
    return holidays.has(m.format('YYYY-MM-DD')); // 공휴일
  }

  // 오늘 기준으로 몇 일 뒤가 영업일인지 계산 //
  function daysUntilBusinessDay(m) {
    let d = m.clone();
    let days = 0;

    while (isOffDay(d)) {
      d.add(1, 'day');
      days++;
    }

    return days;
  }

  // 월별 매출 집계 + 기간 정산액 계산 (SalesCalendarBox 계산식 동일) //
  useEffect(() => {
    const linkopayFeeRate = 0.035;

    // 기간 내 일자 리스트 생성 //
    let dayList = [];
    let dayCursor = startDate.clone().startOf('day');
    const lastDay = endDate.clone().startOf('day');

    while (dayCursor.isSameOrBefore(lastDay, 'day')) {
      dayList.push({
        date: dayCursor.clone(),
        settlementPrice: 0,
        settlementStatus: 'SCHEDULED'
      });
      dayCursor.add(1, 'day');
    }

    // 이미 서버에서 정산 처리된 merchantUid Set (O(1) lookup) //
    const settledUidSet = new Set(settledMerchantUids || []);

    // 프론트 정산 예측 //
    for (let i = 0; i < dayList.length; i++) {
      for (let j = 0; j < yearSales.length; j++) {
        if (dayList[i].date.clone().subtract(settlementCycle, 'days').format('YYYY.MM.DD') === moment(yearSales[j].approvalDate).format('YYYY.MM.DD')
          && moment('2025-09-01').isBefore(moment(yearSales[j].approvalDate))
          && yearSales[j].cardPrice > 0
          && (yearSales[j].merchantUid.split('-')[2] === '1' || yearSales[j].merchantUid.split('-')[2] === '2')
          && !settledUidSet.has(yearSales[j].merchantUid)) {
            let feeAmount = Math.floor(yearSales[j].cardPrice * linkopayFeeRate);

            // 정산일이 휴일/공휴일인 경우 //
            if (isOffDay(moment(dayList[i].date))) {
              let days = daysUntilBusinessDay(moment(dayList[i].date));

              // 기간 일자 데이터 안에 들어있는 경우 //
              if (dayList.length > (i + days)) dayList[i + days].settlementPrice += (yearSales[j].cardPrice - feeAmount);
            }
            // 정산일이 영업일인 경우 //
            else {
              dayList[i].settlementPrice += (yearSales[j].cardPrice - feeAmount);
            }
        }
      }
    }

    // 서버 정산 데이터로 해당 날짜의 프론트 예측 덮어쓰기 //
    for (let i = 0; i < dayList.length; i++) {
      for (let j = 0; j < settlements.length; j++) {
        if (dayList[i].date.clone().format('YYYY.MM.DD') === moment(settlements[j].settledDate).format('YYYY.MM.DD')) {
          dayList[i].settlementPrice = settlements[j].settlementAmount;
          dayList[i].settlementStatus = settlements[j].status;
          break;
        }
      }
    }

    let totalSettlementPrice = 0;
    let totalScheduledSettlementPrice = 0;

    for (let i = 0; i < dayList.length; i++) {
      if (dayList[i].settlementStatus === 'COMPLETED') totalSettlementPrice += dayList[i].settlementPrice;
      else totalScheduledSettlementPrice += dayList[i].settlementPrice;
    }

    // 월별 매출 집계 (빈 월 포함) //
    const monthMap = new Map();
    let monthCursor = startDate.clone().startOf('month');
    const lastMonth = endDate.clone().startOf('month');

    while (monthCursor.isSameOrBefore(lastMonth, 'month')) {
      monthMap.set(monthCursor.format('YYYY.MM'), { date: monthCursor.clone(), salesPrice: 0 });
      monthCursor.add(1, 'month');
    }

    let newPeriodSalesPrice = 0;

    for (let i = 0; i < yearSales.length; i++) {
      const monthKey = moment(yearSales[i].approvalDate).format('YYYY.MM');

      if (monthMap.has(monthKey)) {
        monthMap.get(monthKey).salesPrice += yearSales[i].totalPrice;
        newPeriodSalesPrice += yearSales[i].totalPrice;
      }
    }

    // 매출 내림차순 정렬 //
    const newMonthData = Array.from(monthMap.values());
    newMonthData.sort((a, b) => b.salesPrice - a.salesPrice);

    setMonthData(newMonthData);
    setPeriodSalesPrice(newPeriodSalesPrice);
    setSettlementPrice(totalSettlementPrice);
    setExpectedSettlementPrice(totalScheduledSettlementPrice);

    // bar 리스트 재마운트 → grow 애니메이션 재생 //
    setAnimationKey(prev => prev + 1);

    setTimeout(() => (
      setIsYearLoading(false)
    ), 1000)
  }, [startDate, endDate, yearSales, settlementCycle, settlements, settledMerchantUids, holidays])

  // 기간 타이틀 텍스트 정리 //
  function decidePeriodTitle() {
    const format = t('statistics.periodSales.monthTitleFormat');

    return startDate.format(format) + ' - ' + endDate.format(format);
  }

  // 매출 최댓값 대비 bar 너비 비율 //
  function decideBarWidth(salesPrice, maxPrice) {
    if (maxPrice <= 0) return 0;

    return (salesPrice / maxPrice) * 100;
  }

  // 매출 순위에 따른 bar 색상 //
  function decideBarColor(rank, total) {
    if (total <= 1) return barColors[0];

    const colorIndex = Math.round((rank / (total - 1)) * (barColors.length - 1));

    return barColors[colorIndex];
  }

  // 월별 매출 bar 리스트 렌더 //
  function renderMonthBars() {
    const maxPrice = monthData.length > 0 ? monthData[0].salesPrice : 0;

    return monthData.map((data, index) => (
      <MonthRow key={'period-month-' + animationKey + '-' + index}>
        <MonthLabelBox>
          <Text fontFamily='NotoSansKR-Medium' size={20} color={theme.text} cursor='default'>{data.date.format(t('statistics.periodSales.monthLabelFormat'))}</Text>
        </MonthLabelBox>
        <BarTrack $isDark={isDark}>
          <BarFill $width={decideBarWidth(data.salesPrice, maxPrice)} $color={decideBarColor(index, monthData.length)} />
        </BarTrack>
        <AmountBox>
          <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{data.salesPrice.toLocaleString('ko-KR')}</Text>
          <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
        </AmountBox>
      </MonthRow>
    ));
  }

  return (
    <TopLevelWrapper>
      <PeriodBox $isDark={isDark}>
        <ContentWrapper>
          <TitleWrapper>
            <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{decidePeriodTitle()}</Text>
          </TitleWrapper>
          <InfoComponentBoxWrapper>
            <InfoComponentBox>
              <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodSalesAmount')}</Text>
              <ValueWrapper>
                <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{periodSalesPrice.toLocaleString('ko-KR')}</Text>
                <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
              </ValueWrapper>
            </InfoComponentBox>
            <InfoComponentBox>
              <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodLinkoPaySettlement')}</Text>
              <ValueWrapper>
                <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{settlementPrice.toLocaleString('ko-KR')}</Text>
                <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
              </ValueWrapper>
            </InfoComponentBox>
            <InfoComponentBox>
              <Text fontFamily='NotoSansKR-Medium' size={14} color={theme.text} cursor='default'>{t('statistics.periodSales.periodLinkoPayExpectedSettlement')}</Text>
              <ValueWrapper>
                <Text fontFamily='NotoSansKR-Bold' size={20} color={theme.text} cursor='default'>{expectedSettlementPrice.toLocaleString('ko-KR')}</Text>
                <Text fontFamily='NotoSansKR-Bold' size={14} color={theme.text} cursor='default'>{t('statistics.won')}</Text>
              </ValueWrapper>
            </InfoComponentBox>
          </InfoComponentBoxWrapper>
          <BarListWrapper $isScroll={monthData.length > 12} isNotOpen={!isOpenPartnerGymSales}>
            {
              !isOpenPartnerGymSales &&
              <NotOpenOpacityBox />
            }
            {
              !isOpenPartnerGymSales &&
              <NotOpenBox>
                <Text fontFamily='NotoSansKR-Bold' size={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivate')}</Text>
                <Text fontFamily='NotoSansKR-Regular' size={15} color={theme.subText} cursor='default'>{t('statistics.partnerGymPrivateDesc')}</Text>
              </NotOpenBox>
            }
            {renderMonthBars()}
          </BarListWrapper>
        </ContentWrapper>
      </PeriodBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;
`;

const PeriodBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 20px 40px;
  margin-top: 20px;
  margin-bottom: 39.5px;

  width: calc(100% - 80px);
  min-height: 660px;

  gap: 15px;

  border-radius: 20px;
  background-color: ${props => props.theme.subBackground};
  box-shadow: ${props => props.$isDark ? 'none' : '0px -20px 30px 0px rgba(0, 0, 0, 0.02)'};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 15px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const InfoComponentBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 24px;
`;

const InfoComponentBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;
`;

const BarListWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 20px;

  width: 100%;

  gap: 15px;

  max-height: ${props => props.$isScroll ? '540px' : 'none'};
  overflow-y: ${props => props.$isScroll ? 'auto' : 'visible'};
  padding-right: ${props => props.$isScroll ? '8px' : '0px'};

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    background-color: ${props => props.theme.subBackground};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 1000px;
    background-color: ${props => props.theme.brand500};
  }
`;

const MonthRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const MonthLabelBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  flex-shrink: 0;
  width: 150px;
`;

const BarTrack = styled.div`
  position: relative;
  display: flex;

  align-items: center;

  flex: 1 0 0;
  min-width: 0px;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const barFillGrow = keyframes`
  from {
    width: 0%;
  }
  to {
    width: var(--bar-width);
  }
`;

const BarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  --bar-width: ${props => props.$width}%;
  width: var(--bar-width);
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.$color};

  animation: ${barFillGrow} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const AmountBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  flex-shrink: 0;
  width: 200px;

  gap: 5px;
`;

const NotOpenOpacityBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  opacity: 0.8;
  background-color: ${props => props.theme.background};
`;

const NotOpenBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.size}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  &:hover {
    cursor: ${props => props.cursor};
  }
`;
